import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import BottomNav from '../BottomNav';
import { useAuth } from '../AuthContext';
import { useTheme } from '../../ThemeContext';

const emptyForm = {
  title: '',
  price: '',
  description: '',
  photoMediaId: null,
  photoThumbMediaId: null,
};
const PAGE_SIZE = 30;
const AUTO_LOAD_THRESHOLD_PX = 420;
const LIST_DELETE_ANIMATION_MS = 240;

const appendUniqueById = (prevItems, nextItems) => {
  const seenIds = new Set(prevItems.map((item) => Number(item?.id || 0)));
  const uniqueNext = nextItems.filter((item) => {
    const id = Number(item?.id || 0);
    if (!id || seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });
  return [...prevItems, ...uniqueNext];
};

const MyMarketItemsPage = () => {
  const { theme } = useTheme();
  const {
    getMyMarketItems,
    createMarketItem,
    updateMarketItem,
    deleteMarketItem,
    uploadMedia,
  } = useAuth();
  const isDark = theme === 'dark';
  const themeClass = isDark ? 'bg-dark text-white' : 'bg-light text-dark';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingItemId, setEditingItemId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingItemIds, setDeletingItemIds] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const scrollRafRef = useRef(null);
  const deletingItemTimersRef = useRef(new Map());

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMyMarketItems({ limit: PAGE_SIZE });
      const nextItems = Array.isArray(response?.items) ? response.items : [];
      setItems(nextItems);
      setTotalCount(Number.isFinite(Number(response?.total_count)) ? Number(response.total_count) : nextItems.length);
      setHasMore(nextItems.length >= PAGE_SIZE);
    } catch (ex) {
      console.error('load my market items error', ex);
      setErrorText(ex?.message || 'Не удалось загрузить мои товары');
    } finally {
      setLoading(false);
    }
  }, [getMyMarketItems]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const loadMoreItems = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || items.length === 0) return;
    const beforeId = items[items.length - 1]?.id;
    if (!beforeId) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const response = await getMyMarketItems({ limit: PAGE_SIZE, beforeId });
      const nextItems = Array.isArray(response?.items) ? response.items : [];
      setItems((prev) => appendUniqueById(prev, nextItems));
      if (Number.isFinite(Number(response?.total_count))) {
        setTotalCount(Number(response.total_count));
      }
      if (nextItems.length < PAGE_SIZE) setHasMore(false);
    } catch (ex) {
      console.error('load more my market items error', ex);
      setErrorText(ex?.message || 'Не удалось догрузить мои товары');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [getMyMarketItems, hasMore, items]);

  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const container = scrollRef.current;
      if (!container || loading || loadingMoreRef.current || !hasMore) return;
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceToBottom <= AUTO_LOAD_THRESHOLD_PX) {
        void loadMoreItems();
      }
    });
  }, [hasMore, loadMoreItems, loading]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) window.cancelAnimationFrame(scrollRafRef.current);
      deletingItemTimersRef.current.forEach((timer) => clearTimeout(timer));
      deletingItemTimersRef.current.clear();
    };
  }, []);

  const resetForm = () => {
    setEditingItemId(null);
    setPhotoFile(null);
    setForm(emptyForm);
  };

  const startEdit = (item) => {
    setEditingItemId(item.id);
    setForm({
      title: item.title || '',
      price: String(item.price || ''),
      description: item.description || '',
      photoMediaId: item.photo_media_id || null,
      photoThumbMediaId: item.photo_thumb_media_id || null,
    });

    window.requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrorText('');
    setSuccessText('');

    try {
      let photoMediaId = form.photoMediaId;
      let photoThumbMediaId = form.photoThumbMediaId;

      if (photoFile) {
        const mediaResp = await uploadMedia({ profile: 'market', file: photoFile });
        if (!mediaResp?.status) {
          throw new Error('Не удалось загрузить фото');
        }
        photoMediaId = mediaResp.full?.id || null;
        photoThumbMediaId = mediaResp.thumb?.id || null;
      }

      const payload = {
        title: String(form.title || '').trim(),
        price: Number(form.price || 0),
        description: String(form.description || '').trim() || null,
        photo_media_id: photoMediaId,
        photo_thumb_media_id: photoThumbMediaId,
      };

      if (!payload.title || payload.price <= 0) {
        throw new Error('Заполните название и цену');
      }

      if (editingItemId) {
        const resp = await updateMarketItem(editingItemId, payload);
        if (!resp?.status) throw new Error(resp?.detail || 'Не удалось обновить товар');
        setSuccessText('Товар обновлен');
      } else {
        const resp = await createMarketItem(payload);
        if (!resp?.status) throw new Error(resp?.detail || 'Не удалось добавить товар');
        setSuccessText('Товар добавлен');
      }

      await loadItems();
      resetForm();
    } catch (ex) {
      console.error('save market item error', ex);
      setErrorText(ex?.message || 'Ошибка сохранения товара');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const deletingId = Number(confirmDelete.id || 0);
    if (deletingId <= 0) {
      setConfirmDelete(null);
      return;
    }

    const shouldResetEditor = editingItemId === deletingId;
    setConfirmDelete(null);
    setDeletingItemIds((prev) => (prev.includes(deletingId) ? prev : [...prev, deletingId]));

    if (deletingItemTimersRef.current.has(deletingId)) return;

    const timer = setTimeout(async () => {
      deletingItemTimersRef.current.delete(deletingId);
      try {
        const resp = await deleteMarketItem(deletingId);
        if (!resp?.status) throw new Error('Не удалось удалить товар');
        setItems((prev) => prev.filter((item) => item.id !== deletingId));
        setTotalCount((prev) => (
          Number.isFinite(Number(resp?.total_count))
            ? Number(resp.total_count)
            : Math.max(0, Number(prev || 0) - 1)
        ));
        if (shouldResetEditor) resetForm();
      } catch (ex) {
        console.error('delete market item error', ex);
        setErrorText(ex?.message || 'Ошибка удаления товара');
      } finally {
        setDeletingItemIds((prev) => prev.filter((id) => id !== deletingId));
      }
    }, LIST_DELETE_ANIMATION_MS);

    deletingItemTimersRef.current.set(deletingId, timer);
  };

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={`min-vh-100 pb-5 ${themeClass} scrollable my-market-items-page-shell market-page-shell ${isDark ? 'market-page-shell--dark' : 'market-page-shell--light'}`}
    >
      <div className={`main-status-glass ${isDark ? 'main-status-glass--dark' : 'main-status-glass--light'}`} aria-hidden="true" />

      <div className="market-page-topbar">
        <div className="market-page-topbar__card">
          <div className="market-page-title-block">
            <span className="market-page-kicker">Ваши объявления</span>
            <h1>Мои товары</h1>
            <p>Добавляйте находки, фото и описание для барахолки</p>
          </div>
          <div className="market-page-count" aria-label={`Товаров: ${totalCount}`}>
            {totalCount}
          </div>
        </div>
      </div>

      <Container className="market-page-content pb-5">
        <div className="market-page-topbar-spacer" aria-hidden="true" />

        {errorText && (
          <div className="market-glass-alert market-glass-alert--danger mb-3" role="alert">
            {errorText}
          </div>
        )}
        {successText && (
          <div className="market-glass-alert market-glass-alert--success mb-3" role="status">
            {successText}
          </div>
        )}

        <Card className="market-form-card mb-3">
          <Card.Body className="market-form-body">
            <Card.Title className="market-form-title">{editingItemId ? 'Редактирование товара' : 'Новый товар'}</Card.Title>
            <Form onSubmit={handleSubmit}>
              <Row className="g-2">
                <Col xs={12}>
                  <Form.Control
                    className="market-form-control"
                    placeholder="Название"
                    value={form.title}
                    maxLength={180}
                    onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                    required
                  />
                </Col>
                <Col xs={12}>
                  <Form.Control
                    className="market-form-control"
                    placeholder="Цена (₽)"
                    type="number"
                    min="1"
                    value={form.price}
                    onChange={(e) => setForm((prev) => ({ ...prev, price: e.target.value }))}
                    required
                  />
                </Col>
                <Col xs={12}>
                  <Form.Control
                    className="market-form-control"
                    as="textarea"
                    rows={3}
                    placeholder="Описание"
                    value={form.description}
                    maxLength={4000}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Control
                    className="market-form-control market-file-control"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                  />
                </Col>
                <Col xs={editingItemId ? 6 : 12} className="d-grid">
                  <Button className="market-glass-btn market-glass-btn--primary" variant="light" type="submit" disabled={saving}>
                    {saving ? 'Сохранение...' : editingItemId ? 'Сохранить' : 'Добавить'}
                  </Button>
                </Col>
                {editingItemId && (
                  <Col xs={6} className="d-grid">
                    <Button className="market-glass-btn market-glass-btn--secondary" variant="light" onClick={resetForm} disabled={saving}>
                      Отмена
                    </Button>
                  </Col>
                )}
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="market-loader"><Spinner animation="border" /></div>
        ) : (
          <Row className="g-2">
            {items.map((item) => (
              <Col
                xs={12}
                key={item.id}
                className={`my-market-item-anim ${deletingItemIds.includes(Number(item?.id || 0)) ? 'my-market-item-anim--deleting' : ''}`}
              >
                <Card className="my-market-item-card">
                  <Card.Body className="my-market-item-body">
                    <Row className="align-items-center g-2">
                      <Col xs={3} sm={2}>
                        {item.photo_thumb_url ? (
                          <img
                            src={item.photo_thumb_url}
                            alt={item.title}
                            className="my-market-item-img"
                          />
                        ) : (
                          <div className="my-market-item-img my-market-item-img--empty">Нет фото</div>
                        )}
                      </Col>
                      <Col xs={6} sm={7} className="my-market-item-info">
                        <div className="my-market-item-title">{item.title}</div>
                        <small className="my-market-item-price">{Number(item.price || 0).toLocaleString('ru-RU')} ₽</small>
                      </Col>
                      <Col xs={3} className="d-grid gap-1">
                        <Button
                          size="sm"
                          variant="light"
                          className="market-mini-btn market-mini-btn--edit"
                          onClick={() => startEdit(item)}
                          disabled={deletingItemIds.includes(Number(item?.id || 0))}
                        >
                          Изм.
                        </Button>
                        <Button
                          size="sm"
                          variant="light"
                          className="market-mini-btn market-mini-btn--danger"
                          onClick={() => setConfirmDelete(item)}
                          disabled={deletingItemIds.includes(Number(item?.id || 0))}
                        >
                          Удал.
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))}
            {items.length === 0 && (
              <Col xs={12}>
                <div className="market-empty-card">Товаров пока нет</div>
              </Col>
            )}
            {loadingMore && (
              <Col xs={12}>
                <div className="market-loader market-loader--small"><Spinner animation="border" size="sm" /></div>
              </Col>
            )}
          </Row>
        )}
      </Container>

      <Modal
        show={Boolean(confirmDelete)}
        onHide={() => setConfirmDelete(null)}
        centered
        className={`market-item-modal ${isDark ? 'market-item-modal--dark' : 'market-item-modal--light'}`}
        dialogClassName="market-item-modal-dialog market-confirm-modal-dialog"
        backdropClassName="market-item-modal-backdrop"
        contentClassName={`market-item-modal-content ${isDark ? 'market-item-modal-content--dark' : 'market-item-modal-content--light'}`}
      >
        <Modal.Header closeButton className="market-item-modal-header">
          <Modal.Title className="market-item-modal-title">Удаление товара</Modal.Title>
        </Modal.Header>
        <Modal.Body className="market-confirm-body">Удалить товар "{confirmDelete?.title}"?</Modal.Body>
        <Modal.Footer className="market-confirm-footer">
          <Button className="market-glass-btn market-glass-btn--secondary" variant="light" onClick={() => setConfirmDelete(null)}>Отмена</Button>
          <Button className="market-glass-btn market-glass-btn--danger" variant="light" onClick={handleDelete}>Удалить</Button>
        </Modal.Footer>
      </Modal>

      <BottomNav />
    </div>
  );
};

export default MyMarketItemsPage;
